import { useEffect, useMemo, useRef } from 'react';
import { keepPreviousData } from '@zg-rentals/react-query5';
import {
  useCappsRenterDocumentsWebApiHandler_retrieveActiveDocuments,
  useCappsRenterDocumentsWebApiHandler_setDocumentRedactionStatus,
} from '@zg-rentals/ts-capps-client-react-query';
import type { IncomeDocumentWebApiDto } from '@zg-rentals/ts-capps-client';
import type { Document } from '../models/rental-application';

const MAX_REFETCH_ATTEMPTS = 10;
// Less than 1 second is forbiden by the API spec
const REFETCH_INTERVAL = 6000;

// redactedDocumentId is only populated once the redaction check has finished
function isRedactionCheckComplete(doc: IncomeDocumentWebApiDto) {
  return !!doc.redactedDocumentId;
}

export function toDocument(doc: IncomeDocumentWebApiDto): Document {
  return {
    id: doc.documentId,
    fileName: doc.fileName,
    // A redacted document falls back to the unredacted preview when the redacted one is missing,
    // matching the retrieveSharedDocuments behavior the graphql layer relies on
    previewUrl: doc.isRedacted ? (doc.redactedPreviewUrl ?? doc.previewUrl) : doc.previewUrl,
    isRedacted: doc.isRedacted,
    expirationDateTime: doc.expirationDateUtc ? new Date(doc.expirationDateUtc).toISOString() : null,
    // Until the check has finished we can't tell whether the document can be redacted
    canBeRedacted: isRedactionCheckComplete(doc) ? doc.redactionTags.length > 0 : null,
    isRedactionCheckComplete: isRedactionCheckComplete(doc),
  };
}

/**
 * Keeps polling redaction related fields for the given income documents and returns them with those
 * fields filled in. Documents without an id yet, such as an upload still in flight, are ignored
 */
export function useIncomeDocsRedaction(documents: Array<Document>) {
  // Guards against re-firing redaction for a doc while the request is in flight
  const requestedRedactionIds = useRef(new Set<string>());

  const documentIds = documents.map((doc) => doc.id).filter((id): id is string => !!id);

  const { data } = useCappsRenterDocumentsWebApiHandler_retrieveActiveDocuments(
    { documentIds },
    {
      enabled: documentIds.length > 0,
      // The document ids are part of the query key, without this the redaction info would blank out
      // on every upload and deletion while the new key loads
      placeholderData: keepPreviousData,
      // Poll until every redaction check has finished, giving up after MAX_REFETCH_ATTEMPTS. Adding or
      // removing a document changes the query key, so it starts over with a fresh attempt count
      refetchInterval: ({ state }) =>
        state.data?.documentInfoList.every(isRedactionCheckComplete) || state.dataUpdateCount >= MAX_REFETCH_ATTEMPTS
          ? false
          : REFETCH_INTERVAL,
      meta: {
        tags: ['incomeDocuments'],
        context: { api: 'cappsRenterDocuments', details: 'retrieveActiveDocuments' },
      },
    },
  );

  const allDocs = useMemo(() => data?.documentInfoList.map(toDocument), [data]);

  const { mutate: setDocumentRedactionStatus } = useCappsRenterDocumentsWebApiHandler_setDocumentRedactionStatus({
    meta: {
      invalidates: ['incomeDocuments'],
      context: { api: 'cappsRenterDocuments', details: 'setDocumentRedactionStatus' },
    },
    onError: (_error, variables) => {
      // Un-guard on failure so polling can retry this document
      requestedRedactionIds.current.delete(variables.postData.documentId);
    },
  });

  useEffect(() => {
    // We redact one document at a time, with each refetch we'll move to the next document to redact
    // since the previous one will have isRedacted set to true

    // TODO: Update Web Api to support redaction of multiple docs and return
    // cache-friendly data on the mutation
    const idToRedact = allDocs?.find(
      (doc) =>
        doc.isRedactionCheckComplete &&
        doc.canBeRedacted &&
        !doc.isRedacted &&
        !!doc.id &&
        !requestedRedactionIds.current.has(doc.id),
    )?.id;
    if (!idToRedact) {
      return;
    }

    requestedRedactionIds.current.add(idToRedact);
    setDocumentRedactionStatus({
      postData: {
        documentId: idToRedact,
        isRedacted: true,
      },
    });
  }, [allDocs, setDocumentRedactionStatus]);

  return allDocs;
}
